import { useFormContext } from "react-hook-form";

import { ConsumablesFormStepSection } from "@jti/ui/components/ConsumablesFormStepSection";
import { Image } from "@jti/ui/components/Image";
import { RadioCard } from "@jti/ui/components/RadioCard";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import type { ConsumablesMatrixData } from "@product-details/containers";

import { type ConsumablesFormValues } from "../../ConsumablesForm.types";
import { getMatrixItemByPurchaseTypeAndPackagingFormat } from "../../utils/getMatrixItemByPurchaseTypeAndPackagingFormat";
import { ConsumablesSectionTitle } from "../ConsumablesFormSectionTitle";

import styles from "./ConsumablesPackagingFormatSection.module.scss";

export type ConsumablesPackagingFormatSectionProps = {
  groupName: keyof ConsumablesFormValues;
  step: number;
  onChange: (packagingFormat: ConsumablesFormValues["packagingFormatId"]) => void;
  consumables: ConsumablesMatrixData;
};

export const ConsumablesPackagingFormatSection = ({
  groupName,
  step,
  onChange,
  consumables,
}: ConsumablesPackagingFormatSectionProps) => {
  const { __ } = useTranslations();

  const { register, watch } = useFormContext<ConsumablesFormValues>();

  const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPackagingFormatId = event.target.value;

    onChange(newPackagingFormatId);
  };

  const registerProps = register(groupName as string, { onChange: onChangeHandler });

  const purchaseType = watch("purchaseType");

  return (
    <ConsumablesFormStepSection
      title={
        <ConsumablesSectionTitle translationKey="consumablesForm.section.packaging_format.title" stepCount={step} />
      }
      data-testid="packaging-options"
    >
      {consumables.packagingFormats.map((packagingFormat) => (
        <RadioCard
          key={packagingFormat.id}
          groupName={groupName as string}
          value={packagingFormat.id}
          title={packagingFormat.label}
          subtitle={__("consumablesForm.packagingFormat.description", [
            packagingFormat.packagesPerBundle ?? 1,
            packagingFormat.formatQuantity * (packagingFormat.packagesPerBundle ?? 1),
          ])}
          icon={<Image url={packagingFormat.icon} className={styles.icon} />}
          isDisabled={
            consumables.items.find((consumableItem) => consumableItem.packagingFormatId === packagingFormat.id) ===
            undefined
          }
          isUnavailable={!getMatrixItemByPurchaseTypeAndPackagingFormat(consumables, purchaseType, packagingFormat.id)}
          {...registerProps}
        />
      ))}
    </ConsumablesFormStepSection>
  );
};
